(() => {
  const INTERVAL_MS = 3500;

  // Only on product template
  const isProduct =
    document.body.classList.contains("template-product") ||
    document.documentElement.classList.contains("template-product");
  if (!isProduct) return;

  // Respect reduced motion
  const prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) return;

  let timerId = null;
  let isVisible = !document.hidden;
  let isInView = true; // will be updated by IntersectionObserver
  let userPaused = false;

  let gallery = null;
  let thumbs = [];
  let observer = null;

  const findGallery = () =>
    document.querySelector("media-gallery") ||
    document.querySelector(".media-gallery") ||
    document.querySelector("[data-media-gallery]");

  const getThumbs = (root) => {
    if (!root) return [];
    return Array.from(
      root.querySelectorAll(
        'button[aria-controls], button[data-media-id], button[data-thumbnail], [role="tab"]'
      )
    ).filter(Boolean);
  };

  const getCurrentIndex = (buttons) => {
    for (let i = 0; i < buttons.length; i++) {
      const b = buttons[i];
      const selected =
        b.getAttribute("aria-selected") === "true" ||
        b.classList.contains("is-active") ||
        b.classList.contains("active");
      if (selected) return i;
    }
    return -1;
  };

  const canRun = () =>
    !userPaused && isVisible && isInView && gallery && thumbs.length >= 2;

  const tick = () => {
    if (!canRun()) return;

    // If thumbs changed (variant change, media added), refresh cache occasionally
    // Cheap check: count mismatch
    const currentThumbs = getThumbs(gallery);
    if (currentThumbs.length !== thumbs.length) thumbs = currentThumbs;

    if (thumbs.length < 2) return;

    const currentIndex = getCurrentIndex(thumbs);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % thumbs.length : 0;

    // Avoid throwing if element disappears
    const nextBtn = thumbs[nextIndex];
    if (nextBtn && typeof nextBtn.click === "function") nextBtn.click();
  };

  const stop = () => {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const start = () => {
    if (timerId || !canRun()) return;
    timerId = setInterval(tick, INTERVAL_MS);
  };

  const setupVisibility = () => {
    document.addEventListener("visibilitychange", () => {
      isVisible = !document.hidden;
      if (!isVisible) stop();
      else start();
    });
  };

  const setupInView = () => {
    if (!("IntersectionObserver" in window) || !gallery) return;

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isInView = !!entry && entry.isIntersecting;
        if (!isInView) stop();
        else start();
      },
      { root: null, threshold: 0.2 }
    );

    observer.observe(gallery);
  };

  const setupUserPause = () => {
    const pause = () => {
      userPaused = true;
      stop();
    };

    // If user interacts with the gallery, stop autoplay
    const events = ["pointerdown", "touchstart", "keydown", "wheel"];
    events.forEach((evt) => {
      document.addEventListener(evt, (e) => {
        if (!gallery) return;
        if (gallery.contains(e.target)) pause();
      }, { passive: true });
    });
  };

  const init = () => {
    gallery = findGallery();
    if (!gallery) return;

    thumbs = getThumbs(gallery);
    if (thumbs.length < 2) return;

    setupVisibility();
    setupUserPause();
    setupInView();

    start();
  };

  // Start without competing with LCP/critical JS
  const boot = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 800);
    }
  };

  if (document.readyState === "complete" || document.readyState === "interactive") {
    boot();
  } else {
    window.addEventListener("DOMContentLoaded", boot, { once: true });
  }
})();
